/**
 * Zod schemas for runtime validation. Used by Cloud Functions to validate
 * payloads from the web app (B2 sends settings updates etc.) and by
 * the seed script.
 *
 * Keep these in lock-step with `types.ts`. The pattern is: type lives in
 * `types.ts`, the structural validator lives here.
 */
import { z } from 'zod';
// ── Primitives ────────────────────────────────────────────────────────────
export const E164PhoneSchema = z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, 'Phone number must be E.164 format (e.g. +4915123456789)');
/**
 * E.164 phone number OR empty string. Used in PhoneSettings where the
 * user may want to save partial config (e.g. just the API key) before
 * having a real number. Runtime checks gate actual usage downstream.
 */
export const E164OrEmptySchema = z
    .string()
    .regex(/^(\+[1-9]\d{6,14})?$/, 'Phone number must be E.164 format (e.g. +4915123456789) or empty.');
export const HexColorSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a hex string like #4A90E2');
// ── PhoneSettings ─────────────────────────────────────────────────────────
/**
 * The ConversationRelay / Media-Fork URLs are populated automatically by
 * the voice-agent service from its `VOICE_AGENT_PUBLIC_URL` env var when
 * Twilio/Telnyx hits the inbound webhook — the field in the UI exists
 * only to surface the value for copy/paste. It MAY be empty and we still
 * accept the payload; we also tolerate non-URL strings so paste mistakes
 * don't block save.
 */
const OptionalUrl = z
    .string()
    .max(2048)
    .optional()
    .or(z.literal(''));
/**
 * Twilio + Telnyx config blocks intentionally allow empty string fields
 * so a user can save partial settings (e.g. only the API key, then add
 * Account SID + number later). Validation that *requires* values
 * happens at runtime where the field is actually consumed (purchase
 * flow, voice-agent, etc.).
 */
const TwilioConfigSchema = z.object({
    accountSid: z.string().max(80).default(''),
    phoneNumber: E164OrEmptySchema.default(''),
    conversationRelayUrl: OptionalUrl,
    smsEnabled: z.boolean().optional(),
    messagingServiceSid: z.string().max(80).optional(),
    whatsappEnabled: z.boolean().optional(),
    whatsappFrom: z.string().max(80).optional(),
});
const TelnyxConfigSchema = z.object({
    connectionId: z.string().max(80).default(''),
    phoneNumber: E164OrEmptySchema.default(''),
    mediaForkUrl: OptionalUrl,
    smsEnabled: z.boolean().optional(),
    messagingProfileId: z.string().max(80).optional(),
    whatsappEnabled: z.boolean().optional(),
    whatsappFrom: z.string().max(80).optional(),
});
const FillerCategorySchema = z.enum([
    'breath',
    'mhm',
    'filler-word',
    'wait-phrase',
    'throat-clear',
    'cough',
]);
const FillerCategoryConfigSchema = z.object({
    category: FillerCategorySchema,
    enabled: z.boolean(),
    minDelayMs: z.number().int().min(0).max(10_000),
    maxPerCall: z.number().int().min(0).max(20),
});
// ── Booking Slots ─────────────────────────────────────────────────────────
/**
 * One named, parallel resource on the user's calendar. Slots model
 * "overbooking" capacity: N slots = N appointments can run at the same
 * clock time without stepping on each other.
 *
 * Use cases:
 *   - hair salon → one slot per stylist ("Barbara", "Anna", "Lisa").
 *   - dental practice → one slot per service line ("Mundhygiene",
 *     "Prophylaxe").
 *
 * The AI maps a caller intent to a slot by name (exact > fuzzy) and by
 * the optional `eventTypeIds` filter. When no preference can be derived,
 * the agent picks the slot with the lowest day-utilisation.
 *
 * Slot identity is stored on each calendar event in
 *   - Google: `extendedProperties.private.cp3SlotId`
 *   - Microsoft: trailing `<!--cp3-meta:cp3SlotId=…-->` comment
 * and mirrored back into Firestore `mirrorEvents/*.slotId` on sync.
 */
export const BookingSlotColorToneSchema = z.enum([
    'slate',
    'sky',
    'emerald',
    'amber',
    'rose',
    'violet',
]);
export const BookingSlotSchema = z.object({
    /** Stable ID — slug of the name on first save, then immutable. */
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(60),
    /** Optional event-type whitelist. Empty array / undefined = any type. */
    eventTypeIds: z.array(z.string().min(1).max(64)).max(50).optional(),
    colorTone: BookingSlotColorToneSchema,
    /** Free-form note shown only to the operator (not to callers / AI). */
    note: z.string().max(200).optional(),
});
/**
 * Maximum slots per tenant. 20 covers any plausible salon / clinic and
 * keeps the Firestore doc well under 1 MiB.
 */
export const BookingSlotsArraySchema = z.array(BookingSlotSchema).max(20);
// ──────────────────────────────────────────────────────────────────────
// Call-transfer directory + trusted-caller whitelist (B-Transfer feature).
// ──────────────────────────────────────────────────────────────────────
/**
 * E.164 normaliser — accepts user-entered numbers like `+49 660 388 68 30`
 * or `0660 388 68 30` and returns a strict `+\d{8,16}` form. Throws via
 * Zod `.refine` when the input cannot be reduced to E.164 (e.g. a name
 * pasted into the phone field).
 */
const E164_REGEX = /^\+[1-9]\d{7,15}$/;
const PhoneE164Schema = z
    .string()
    .min(7)
    .max(20)
    .transform((v) => v.replace(/[\s\-().]/g, ''))
    .refine((v) => E164_REGEX.test(v), {
    message: 'Telefonnummer muss im E.164-Format vorliegen (z. B. +49…).',
});
/**
 * `displayName` is the spoken handle the AI matches against. We allow
 * single-name entries (e.g. "Fabian") because some tenants legitimately
 * have only one person of that name. The voice-agent matcher adapts:
 *   • single-token directory entry → a single-token spoken match suffices
 *     (still must be unique within the directory).
 *   • multi-token directory entry → the strict "first AND last name"
 *     gate kicks in, mirroring the original security model.
 */
export const TransferTargetSchema = z.object({
    id: z.string().min(1).max(64),
    displayName: z
        .string()
        .min(2)
        .max(80)
        .refine((v) => v.trim().length >= 2, {
        message: 'Bitte einen Namen eintragen.',
    }),
    phoneNumber: PhoneE164Schema,
    role: z.string().max(60).optional(),
});
export const TrustedCallerSchema = z.object({
    id: z.string().min(1).max(64),
    phoneNumber: PhoneE164Schema,
    displayName: z.string().max(80).optional(),
});
export const TransferModeSchema = z.enum(['off', 'directory', 'fixed', 'directory+fixed']);
export const PhoneSettingsUpdateSchema = z
    .object({
    telephonyProvider: z.enum(['twilio', 'telnyx']),
    twilio: TwilioConfigSchema.optional(),
    telnyx: TelnyxConfigSchema.optional(),
    llmProvider: z.enum([
        'openai',
        'anthropic',
        'google',
        'groq',
        'mistral',
        'openrouter',
        'xai',
    ]),
    llmModel: z.string().min(1).max(120),
    llmTemperature: z.number().min(0).max(1),
    llmMaxTokens: z.number().int().min(50).max(2000),
    systemPrompt: z.string().min(1).max(8000),
    knowledgeBase: z.string().max(20_000).nullable().optional(),
    /** First sentence the caller hears, pre-rendered with the user's
     *  ElevenLabs voice and played back via Twilio `<Play>`. Avoids the
     *  2-3s silence between Twilio answer and our WS pipeline starting,
     *  and replaces the generic "Einen Moment bitte" with a per-account
     *  branded greeting. */
    introPhrase: z.string().max(500).optional(),
    /** Public URL to the rendered intro audio (mp3). Server-managed —
     *  ignored on input. */
    introAudioUrl: z.string().max(2048).nullable().optional(),
    /** Closing line spoken right before the bot hangs up via the
     *  `end_call` tool. Empty falls back to a static default. */
    outroPhrase: z.string().max(500).nullable().optional(),
    /** Public URL to the rendered outro audio (WAV). Server-managed —
     *  ignored on input. */
    outroAudioUrl: z.string().max(2048).nullable().optional(),
    /** Reference into `/agentPresets/{id}`. When set AND the user is on
     *  a pro-tier plan, the pipeline loads the preset's technical
     *  settings and ignores the per-user equivalents. Pass `null` to
     *  clear the selection. */
    selectedAgentPresetId: z.string().min(1).max(64).nullable().optional(),
    /** Toggle on the LLM-Tab: wenn `true` schreibt die UI eine
     *  beratende Variante des Conversation-Style-Prompts in
     *  `promptConversationStyle`; bei `false` eine sachlich-effiziente.
     *  Der voice-agent liest weiter nur `promptConversationStyle` —
     *  dieser Boolean ist reine UI-Memory, damit der Toggle nach
     *  Reload den richtigen Zustand hat. */
    consultativeMode: z.boolean().nullable().optional(),
    /** Five overrideable behaviour-rule blocks that get appended to the
     *  system prompt at call-time. Empty / null = use the corresponding
     *  DEFAULT_PROMPT_* constant from promptDefaults. The "*Security"
     *  ones are GDPR-relevant and the UI hides them behind an
     *  admin-unlock toggle to prevent accidental edits. */
    promptCallerIdSecurity: z.string().max(8000).nullable().optional(),
    promptConversationStyle: z.string().max(8000).nullable().optional(),
    promptAppointmentWorkflow: z.string().max(8000).nullable().optional(),
    promptTicketCreation: z.string().max(8000).nullable().optional(),
    promptTicketLookup: z.string().max(8000).nullable().optional(),
    promptTransferRules: z.string().max(8000).nullable().optional(),
    /** Named transfer destinations the AI may dial. Hard-capped at 30
     *  so the system prompt + Firestore doc stay well-bounded. */
    transferTargets: z.array(TransferTargetSchema).max(30).optional(),
    /** Whitelist of inbound CallerIDs that bypass the strict full-name
     *  gate. Same cap. */
    trustedCallers: z.array(TrustedCallerSchema).max(30).optional(),
    /** Operating mode for the transfer feature. Default 'off'. */
    transferMode: TransferModeSchema.default('off').optional(),
    /** When mode includes `fixed`, the id of the chosen target. Must
     *  resolve to a member of `transferTargets`. */
    transferFallbackTargetId: z.string().max(64).nullable().optional(),
    sttProvider: z.enum(['deepgram', 'elevenlabs']),
    ttsProvider: z.literal('elevenlabs'),
    ttsVoiceId: z.string().min(1),
    ttsModel: z.enum(['eleven_flash_v2_5', 'eleven_multilingual_v2']),
    ttsStability: z.number().min(0).max(1),
    ttsSimilarityBoost: z.number().min(0).max(1),
    fillerEnabled: z.boolean(),
    fillerThresholdMs: z.number().int().min(300).max(2000),
    fillerStrategy: z.enum(['random', 'context-aware', 'sequential']),
    fillerCategories: z.array(FillerCategoryConfigSchema),
    maxFillerPerCall: z.number().int().min(0).max(20),
    /** When true, FillerInjector fires immediately after every
     *  user-stop-speaking, regardless of LLM latency. Useful for
     *  smoother conversational rhythm even with fast LLMs. */
    fillerAlwaysPlay: z.boolean().default(false).optional(),
    /** Admin-only switch: when true, the voice-agent skips the
     *  per-user `KEY__uid` secret lookup and reads the platform-wide
     *  `KEY` directly. Designed for the second admin tier (admins who
     *  want to ride the platform's central API-keys instead of paying
     *  for their own provider plans). Pro/Enterprise users always use
     *  central keys regardless of this flag (their settings UI never
     *  shows the API-Schlüssel section); Ultimate users always BYO. */
    useCentralApiKeys: z.boolean().default(false).optional(),
    /** Speech-to-speech voice-agent toggle. Defaults to classic so
     *  existing deployments keep their behaviour. */
    voiceAgentMode: z.enum(['classic', 'native']).default('classic').optional(),
    nativeVoiceAgent: z
        .object({
        provider: z.enum(['xai', 'openai', 'google']),
        model: z.string().min(1).max(120),
        voice: z.string().min(1).max(80),
    })
        .nullable()
        .optional(),
    language: z.enum(['de-DE', 'de-AT', 'en-US', 'multi']),
    /** Parallel booking slots (overbooking capacity). Empty array =
     *  single implicit "Standard" slot — preserves single-resource
     *  behaviour for existing tenants without forcing a migration. */
    bookingSlots: BookingSlotsArraySchema.optional(),
    // ── LiveKit add-on ─────────────────────────────────────────────────
    /** Which transport carries the audio. Default `twilio-media` keeps
     *  the existing Twilio ConversationRelay WebSocket path; `telnyx-media`
     *  does the same for Telnyx Media Fork. `livekit` routes all of the
     *  tenant's inbound calls through their own LiveKit Cloud project
     *  (SIP-Inbound trunk → dispatch rule → Pipecat agent joining the
     *  same room). Browser test-chat picks its transport independently. */
    pipelineTransport: z
        .enum(['twilio-media', 'telnyx-media', 'livekit'])
        .default('twilio-media')
        .optional(),
    /** LiveKit Cloud project URL — non-secret, paired with the
     *  per-user `LIVEKIT_API_KEY__<uid>` / `LIVEKIT_API_SECRET__<uid>`
     *  secrets. Empty until the tenant configures it in the UI. */
    livekitUrl: z.string().max(2048).nullable().optional(),
    /** The SIP-Inbound URI shown to the user so they can point their
     *  Twilio Elastic SIP Trunk at it. Format
     *  `sip:<project>.sip.livekit.cloud`. Server-derived on first
     *  successful probe; surfaced read-only in the settings UI. */
    livekitSipUri: z.string().max(512).nullable().optional(),
    // Optional separate secret fields — sent on update, NEVER stored in
    // Firestore. Cloud Function moves them to Secret Manager. Accepts
    // `null` too because Firebase's callable JSON serializer converts
    // `undefined` → `null` over the wire, which would otherwise reject
    // every save after the first one (the UI clears `secrets` to
    // undefined post-save to avoid accidental re-upload).
    secrets: z
        .object({
        twilioAuthToken: z.string().nullable().optional(),
        telnyxApiKey: z.string().nullable().optional(),
        livekitApiKey: z.string().nullable().optional(),
        livekitApiSecret: z.string().nullable().optional(),
        livekitSipUsername: z.string().nullable().optional(),
        livekitSipPassword: z.string().nullable().optional(),
    })
        .nullable()
        .optional(),
})
    .refine((s) => (s.telephonyProvider === 'twilio' && !!s.twilio) ||
    (s.telephonyProvider === 'telnyx' && !!s.telnyx), { message: 'Provider config block is required for the selected provider.' })
    .refine((s) => {
    // `fixed` and `directory+fixed` need a fallback target that
    // actually exists in `transferTargets`; otherwise the AI would
    // know it should transfer somewhere but the dispatcher would
    // resolve to nothing.
    const mode = s.transferMode ?? 'off';
    if (mode !== 'fixed' && mode !== 'directory+fixed')
        return true;
    const id = s.transferFallbackTargetId;
    if (!id)
        return false;
    return (s.transferTargets ?? []).some((t) => t.id === id);
}, {
    message: 'Im Modus „fixe Fallback-Nummer" muss eine Zielperson aus der Liste als Fallback ausgewählt sein.',
    path: ['transferFallbackTargetId'],
})
    .refine((s) => {
    // No duplicate phone numbers inside the directory — the LLM
    // gets confused, and our slug match becomes ambiguous.
    const nums = (s.transferTargets ?? []).map((t) => t.phoneNumber);
    return new Set(nums).size === nums.length;
}, {
    message: 'Eine Telefonnummer darf nur einmal in der Weiterleitungsliste stehen.',
    path: ['transferTargets'],
})
    .refine((s) => {
    // Two identical display names → the matcher can't disambiguate
    // a spoken "Fabian" between them, so the AI would refuse every
    // transfer attempt. Catch this at save time so the admin sees
    // the conflict in the UI rather than at call time.
    const names = (s.transferTargets ?? []).map((t) => t.displayName.trim().toLowerCase());
    return new Set(names).size === names.length;
}, {
    message: 'Zwei Zielpersonen tragen denselben Namen — die KI könnte sie nicht unterscheiden. Ergänze einen Nachnamen oder eine Funktion.',
    path: ['transferTargets'],
});
// ── EventType ─────────────────────────────────────────────────────────────
const TimeBlockSchema = z
    .object({
    weekday: z.number().int().min(0).max(6),
    startMinutes: z.number().int().min(0).max(24 * 60 - 1),
    endMinutes: z.number().int().min(1).max(24 * 60),
})
    .refine((b) => b.endMinutes > b.startMinutes, {
    message: 'endMinutes must be > startMinutes',
});
export const EventTypeUpsertSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    durationMinutes: z.number().int().min(5).max(480),
    bufferMinutes: z.number().int().min(0).max(120),
    color: HexColorSchema,
    timeBlocks: z.array(TimeBlockSchema).min(1),
    aiKeywords: z.array(z.string().min(1).max(40)).max(20),
    aiDescription: z.string().min(1).max(280),
    active: z.boolean(),
});
// ── Mirror verification (the only field set users may write client-side) ──
export const MirrorVerificationSchema = z.object({
    eventId: z.string().min(1),
    verified: z.boolean(),
    actualDurationMinutes: z.number().int().min(1).max(720).optional(),
    effectivenessScore: z.number().int().min(0).max(100).optional(),
    notes: z.string().max(2000).optional(),
});
// ── Filler upload metadata (file itself goes through Storage upload URL) ──
export const FillerUpsertSchema = z.object({
    id: z.string().min(1).max(64),
    category: FillerCategorySchema,
    label: z.string().min(1).max(80),
    language: z.string().min(2).max(8),
    triggerContexts: z.array(z.enum(['thinking', 'tool-call', 'long-llm', 'greeting-pause'])),
    active: z.boolean(),
    weight: z.number().min(0).max(1),
});
// ── Admin / membership ────────────────────────────────────────────────────
export const MembershipUpdateSchema = z.object({
    uid: z.string().min(1),
    tier: z.enum(['trial', 'free', 'pro', 'enterprise', 'ultimate']).optional(),
    status: z.enum(['active', 'expired', 'cancelled', 'paused']).optional(),
    /** Epoch milliseconds — null clears (= unlimited). */
    validFromMs: z.number().int().optional(),
    validUntilMs: z.number().int().nullable().optional(),
    notes: z.string().max(2000).optional(),
    /** Optional reference into /membershipPlans. When set the server resolves the
     *  plan and auto-fills tier and validUntil unless the admin explicitly
     *  overrides them in the same payload. Pass `null` to clear the link. */
    planId: z.string().min(1).max(80).nullable().optional(),
});
// ── Membership plans (admin catalogue) ───────────────────────────────────
export const MembershipPlanUpsertSchema = z.object({
    id: z.string().min(1).max(64).regex(/^[a-zA-Z0-9_-]+$/, 'Nur Buchstaben, Zahlen, _ und -.'),
    name: z.string().min(1).max(120),
    tier: z.enum(['trial', 'free', 'pro', 'enterprise', 'ultimate']),
    description: z.string().max(2000).default(''),
    priceMonthlyCents: z.number().int().min(0).max(10_000_000),
    currency: z.enum(['EUR', 'USD']).default('EUR'),
    durationDays: z.number().int().min(1).max(3650).nullable(),
    features: z.array(z.string().min(1).max(160)).max(30).default([]),
    callMinutesIncluded: z.number().int().min(0).max(1_000_000).nullable(),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(10_000).default(100),
    // SaaS-Anfänger-Felder (optional, nur die zwei Anfänger-Pläne setzen sie).
    saasKind: z.enum(['subscription', 'payg']).nullish(),
    overagePerMinuteNetCents: z.number().int().min(0).max(10_000).nullish(),
    perMinuteNetCents: z.number().int().min(0).max(10_000).nullish(),
    defaultTopUpNetCents: z.number().int().min(100).max(1_000_000).nullish(),
    minTopUpNetCents: z.number().int().min(50).max(1_000_000).nullish(),
    // Public/private visibility for the user-facing PlanSwitchCard.
    // Admin-only catalogue listing ignores this gate.
    visibility: z
        .object({
        mode: z.enum(['public', 'private']),
        allowedUids: z.array(z.string().min(1).max(64)).max(500),
    })
        .optional(),
});
// ── Agent Presets (admin catalogue, pro-user picker) ──────────────────────
export const AgentPresetUpsertSchema = z.object({
    id: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-zA-Z0-9_-]+$/, 'Nur Buchstaben, Zahlen, _ und -.'),
    name: z.string().min(1).max(120),
    description: z.string().max(500).default(''),
    icon: z.string().max(8).optional(),
    sortOrder: z.number().int().min(0).max(10_000).default(100),
    archived: z.boolean().default(false),
    // LLM
    llmProvider: z.enum([
        'openai',
        'anthropic',
        'google',
        'groq',
        'mistral',
        'openrouter',
        'xai',
    ]),
    llmModel: z.string().min(1).max(120),
    llmTemperature: z.number().min(0).max(1),
    llmMaxTokens: z.number().int().min(50).max(2000),
    systemPrompt: z.string().min(1).max(8000),
    // STT / TTS
    sttProvider: z.enum(['deepgram', 'elevenlabs']),
    ttsProvider: z.literal('elevenlabs'),
    ttsVoiceId: z.string().min(1),
    ttsModel: z.enum(['eleven_flash_v2_5', 'eleven_multilingual_v2']),
    ttsStability: z.number().min(0).max(1),
    ttsSimilarityBoost: z.number().min(0).max(1),
    // Filler
    fillerEnabled: z.boolean(),
    fillerThresholdMs: z.number().int().min(300).max(2000),
    fillerStrategy: z.enum(['random', 'context-aware', 'sequential']),
    fillerCategories: z.array(FillerCategoryConfigSchema),
    maxFillerPerCall: z.number().int().min(0).max(20),
    fillerAlwaysPlay: z.boolean().default(false).optional(),
    // Voice-agent mode
    voiceAgentMode: z.enum(['classic', 'native']).default('classic').optional(),
    nativeVoiceAgent: z
        .object({
        provider: z.enum(['xai', 'openai', 'google']),
        model: z.string().min(1).max(120),
        voice: z.string().min(1).max(80),
    })
        .nullable()
        .optional(),
    // Locale
    language: z.enum(['de-DE', 'de-AT', 'en-US', 'multi']),
    // Behaviour-rule overrides
    promptCallerIdSecurity: z.string().max(8000).nullable().optional(),
    promptConversationStyle: z.string().max(8000).nullable().optional(),
    promptAppointmentWorkflow: z.string().max(8000).nullable().optional(),
    promptTicketCreation: z.string().max(8000).nullable().optional(),
    promptTicketLookup: z.string().max(8000).nullable().optional(),
});
// ── Onboarding wizard (per-user 12-step answers) ───────────────────────────
const TimeStringSchema = z
    .string()
    .regex(/^([01]?\d|2[0-3]):[0-5]\d$/, 'Format HH:MM (24h).');
export const OnboardingOpeningHourSchema = z.object({
    weekday: z.number().int().min(0).max(6),
    closed: z.boolean().default(false),
    openTime: TimeStringSchema.optional(),
    closeTime: TimeStringSchema.optional(),
    breakStart: TimeStringSchema.optional(),
    breakEnd: TimeStringSchema.optional(),
});
export const OnboardingSpecialClosureSchema = z.object({
    label: z.string().min(1).max(80),
    fromDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    toDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    note: z.string().max(280).optional(),
});
export const OnboardingOfferingSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    durationMinutes: z.number().int().min(0).max(480).optional(),
    priceNote: z.string().max(120).optional(),
});
export const OnboardingFAQSchema = z.object({
    question: z.string().min(1).max(280),
    answer: z.string().min(1).max(2000),
});
/**
 * Saved on every change while the wizard is open. Every field is
 * optional so the user can leave a step blank and still save the
 * partial doc.
 */
export const OnboardingSaveSchema = z.object({
    companyName: z.string().max(160).optional(),
    industry: z.string().max(160).optional(),
    ownerName: z.string().max(160).optional(),
    companyDescription: z.string().max(800).optional(),
    addressStreet: z.string().max(160).optional(),
    addressZip: z.string().max(20).optional(),
    addressCity: z.string().max(120).optional(),
    addressCountry: z.string().max(60).optional(),
    publicEmail: z.string().email().or(z.literal('')).optional(),
    website: z.string().max(2048).optional(),
    openingHours: z.array(OnboardingOpeningHourSchema).max(7).optional(),
    specialClosures: z.array(OnboardingSpecialClosureSchema).max(20).optional(),
    emergencyContact: z.string().max(160).optional(),
    offerings: z.array(OnboardingOfferingSchema).max(40).optional(),
    pricingPolicy: z.enum(['mention', 'mention_range', 'no_mention']).optional(),
    pricingNotes: z.string().max(1000).optional(),
    bookingMode: z.enum(['direct', 'request_only', 'no_booking']).optional(),
    bookingNotes: z.string().max(1000).optional(),
    faq: z.array(OnboardingFAQSchema).max(50).optional(),
    emergencyDefinition: z.string().max(1000).optional(),
    emergencyHandling: z.string().max(1000).optional(),
    documentIds: z.array(z.string().max(80)).max(20).optional(),
    addressing: z.enum(['du', 'sie']).optional(),
    tonality: z.enum(['formal', 'casual']).optional(),
    consultativeMode: z.boolean().optional(),
    completedSteps: z.array(z.number().int().min(0).max(20)).max(20).optional(),
});
/** Admin-only: edit which steps are active + which Gemini model compresses. */
export const OnboardingConfigUpsertSchema = z.object({
    geminiModel: z.string().min(1).max(120).default('gemini-2.5-flash'),
    enabledForTiers: z
        .array(z.enum(['trial', 'free', 'pro', 'enterprise', 'ultimate']))
        .max(5)
        .optional(),
    steps: z
        .array(z.object({
        key: z.string().min(1).max(40),
        label: z.string().min(1).max(80),
        enabled: z.boolean(),
        helperText: z.string().max(500).optional(),
    }))
        .max(20),
});
/** Doc-Upload metadata save (the binary itself goes through Storage). */
export const OnboardingDocSaveSchema = z.object({
    id: z.string().min(1).max(80),
    fileName: z.string().min(1).max(256),
    mimeType: z
        .string()
        .regex(/^application\/(pdf|vnd\.openxmlformats-officedocument\.wordprocessingml\.document|msword)$/, 'Nur PDF oder DOCX.'),
    storagePath: z.string().min(1).max(512),
    sizeBytes: z.number().int().min(1).max(50 * 1024 * 1024).optional(),
});
export const SetUserRoleSchema = z.object({
    uid: z.string().min(1),
    role: z.enum(['admin', 'user']),
});
// ── Profile (name fields) ────────────────────────────────────────────────
const NameField = z
    .string()
    .max(80)
    .transform((s) => s.trim())
    .or(z.literal('').transform(() => ''));
/** Self-edit: a signed-in user updates their own name fields. */
export const SetMyProfileSchema = z.object({
    firstName: NameField.optional(),
    lastName: NameField.optional(),
    /** Optional — when present we also rewrite the displayName so the
     *  Sidebar/Header pick it up immediately. */
    displayName: NameField.optional(),
});
/** Admin-edit: targets `uid`. */
export const AdminSetUserProfileSchema = SetMyProfileSchema.extend({
    uid: z.string().min(1),
});
// ── Verification documents ──────────────────────────────────────────────
export const VerificationDocTypeSchema = z.enum([
    'passport',
    'companyCert',
    'utilityBill',
]);
/**
 * Saves the metadata for a freshly-uploaded verification doc. The
 * binary itself is uploaded directly to Storage by the client; this
 * callable just persists the descriptor.
 */
export const SaveVerificationDocSchema = z.object({
    type: VerificationDocTypeSchema,
    storagePath: z.string().min(1).max(512),
    fileName: z.string().min(1).max(256),
    fileType: z
        .string()
        .regex(/^(image\/(png|jpe?g|webp)|application\/pdf)$/, 'Nur PNG, JPG, WEBP oder PDF.'),
    sizeBytes: z
        .number()
        .int()
        .min(1)
        .max(10 * 1024 * 1024, 'Maximal 10 MB.'),
    /** Epoch ms — only used for utilityBill; older than 90 days is rejected. */
    documentDateMs: z.number().int().nullable().optional(),
});
export const AdminGetVerificationDocsSchema = z.object({
    uid: z.string().min(1),
});
// ── Tickets ───────────────────────────────────────────────────────────────
export const TicketCategorySchema = z.enum([
    'support',
    'billing',
    'technical',
    'feedback',
    'sales',
    'complaint',
    'general',
]);
export const TicketPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
export const TicketStatusSchema = z.enum([
    'open',
    'in-progress',
    'waiting-customer',
    'deferred',
    'resolved',
    'closed',
]);
const EmailListSchema = z
    .array(z.string().email().max(254))
    .max(20)
    .default([]);
/** Internal callable used by the voice-agent service. Auth via service account. */
export const CreateTicketInternalSchema = z.object({
    uid: z.string().min(1),
    subject: z.string().min(1).max(160),
    description: z.string().min(1).max(8000),
    category: TicketCategorySchema.default('general'),
    priority: TicketPrioritySchema.default('normal'),
    callId: z.string().min(1).max(80).optional(),
    callSummary: z.string().max(4000).optional(),
    callerName: z.string().max(160).optional(),
    callerPhone: z.string().max(40).optional(),
    callerEmail: z.string().email().max(254).optional(),
    /** Epoch ms — deferred tickets surface only after this date. */
    deferUntilMs: z.number().int().optional(),
    /** Epoch ms — explicit due date overriding the SLA default. */
    dueAtMs: z.number().int().optional(),
    tags: z.array(z.string().min(1).max(40)).max(20).optional(),
});
/** User-callable: create a ticket from the web UI. */
export const CreateTicketSchema = CreateTicketInternalSchema.omit({ uid: true });
export const UpdateTicketSchema = z.object({
    ticketId: z.string().min(1),
    status: TicketStatusSchema.optional(),
    priority: TicketPrioritySchema.optional(),
    category: TicketCategorySchema.optional(),
    subject: z.string().min(1).max(160).optional(),
    description: z.string().min(1).max(8000).optional(),
    assigneeUid: z.string().nullable().optional(),
    dueAtMs: z.number().int().nullable().optional(),
    deferUntilMs: z.number().int().nullable().optional(),
    tags: z.array(z.string().min(1).max(40)).max(20).optional(),
    callerEmail: z.string().email().max(254).nullable().optional(),
});
export const AddTicketCommentSchema = z.object({
    ticketId: z.string().min(1),
    body: z.string().min(1).max(8000),
    /** When true, the comment is also delivered to the caller via email. */
    sendToCustomer: z.boolean().default(false),
});
export const ListTicketsSchema = z.object({
    status: z.array(TicketStatusSchema).optional(),
    priority: z.array(TicketPrioritySchema).optional(),
    category: z.array(TicketCategorySchema).optional(),
    /** Default 50, max 200. */
    limit: z.number().int().min(1).max(200).optional(),
    /** Hide deferred tickets whose deferUntil is in the future. Default true. */
    hideDeferred: z.boolean().optional(),
    /** Free-text search over subject + description. */
    search: z.string().max(120).optional(),
    /** When set, only tickets from this assignee. Empty string → unassigned. */
    assigneeUid: z.string().nullable().optional(),
});
// ── Phone-number shop ────────────────────────────────────────────────────
export const NumberCountrySchema = z.enum(['DE', 'AT', 'CH']);
export const NumberTypeSchema = z.enum(['local', 'mobile', 'tollfree']);
export const NumberProviderSchema = z.enum(['twilio', 'telnyx']);
export const SearchAvailableNumbersSchema = z.object({
    provider: NumberProviderSchema.default('twilio'),
    country: NumberCountrySchema,
    type: NumberTypeSchema.default('local'),
    /** Local area-code or city prefix (e.g. "30" for Berlin). */
    areaCode: z.string().regex(/^\d{1,6}$/).optional(),
    /** Substring that must appear anywhere in the number. */
    contains: z.string().regex(/^[0-9*]{1,12}$/).optional(),
    voiceEnabled: z.boolean().default(true),
    smsEnabled: z.boolean().optional(),
    limit: z.number().int().min(1).max(50).default(20),
});
export const PurchaseNumberSchema = z.object({
    provider: NumberProviderSchema.default('twilio'),
    /** E.164 number returned by `searchAvailableNumbers`. */
    phoneNumber: z.string().regex(/^\+[1-9]\d{6,14}$/),
    /** When true, the new number replaces the user's current PhoneSettings number. */
    setAsActive: z.boolean().default(true),
});
/**
 * Reference to an existing number on the provider's account.
 *   - Twilio: IncomingPhoneNumber SID (`PN…`)
 *   - Telnyx: phone_number ID (numeric string)
 */
export const ReleaseNumberSchema = z.object({
    provider: NumberProviderSchema.default('twilio'),
    numberRef: z.string().min(1).max(64),
});
export const ReconfigureNumberWebhookSchema = z.object({
    provider: NumberProviderSchema.default('twilio'),
    numberRef: z.string().min(1).max(64),
});
export const ListMyNumbersSchema = z.object({
    provider: NumberProviderSchema.default('twilio'),
});
const NotificationDelaySchema = z.enum([
    'immediate',
    '1min',
    '5min',
    '15min',
    '1h',
    'endOfDay',
]);
const NotificationChannelConfigSchema = z.object({
    enabled: z.boolean(),
    templateId: z.string().max(64),
    sendDelay: NotificationDelaySchema,
});
const TriggerNotificationConfigSchema = z.object({
    email: NotificationChannelConfigSchema,
    whatsapp: NotificationChannelConfigSchema,
    sms: NotificationChannelConfigSchema,
});
const ZapierWebhookEntrySchema = z.object({
    enabled: z.boolean(),
    url: z
        .string()
        .trim()
        .max(2048)
        .refine((v) => v === '' || /^https:\/\/.+/i.test(v), 'URL muss mit https:// beginnen.'),
});
const ZapierWebhookConfigSchema = z.object({
    ticketCreated: ZapierWebhookEntrySchema,
    ticketStatusChanged: ZapierWebhookEntrySchema,
    appointmentBooked: ZapierWebhookEntrySchema,
});
export const TicketSettingsUpdateSchema = z.object({
    enabled: z.boolean().optional(),
    recipientEmails: EmailListSchema.optional(),
    ccEmails: EmailListSchema.optional(),
    highPriorityRecipientEmails: EmailListSchema.optional(),
    urgentPriorityRecipientEmails: EmailListSchema.optional(),
    replyToEmail: z.string().email().or(z.literal('')).optional(),
    fromName: z.string().max(120).optional(),
    customerOutboundEnabled: z.boolean().optional(),
    customerEmailSignature: z.string().max(2000).optional(),
    autoConfirmCallerOnCreate: z.boolean().optional(),
    autoConfirmTemplateId: z.string().max(64).optional(),
    ticketCreatedNotify: TriggerNotificationConfigSchema.optional(),
    appointmentBookedNotify: TriggerNotificationConfigSchema.optional(),
    zapierWebhooks: ZapierWebhookConfigSchema.optional(),
    slaHours: z
        .object({
        low: z.number().min(0).max(720),
        normal: z.number().min(0).max(720),
        high: z.number().min(0).max(720),
        urgent: z.number().min(0).max(720),
    })
        .optional(),
    escalationEnabled: z.boolean().optional(),
    escalationAfterHours: z.number().min(0).max(168).optional(),
    escalationRecipientEmails: EmailListSchema.optional(),
});
//# sourceMappingURL=schemas.js.map